import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { BaseSuspenseQueryOpts } from '../baseTypes';
import {
  AllCollaborativeChartsReviewsParams,
  ChartsReviewItemQuestionnaireFields,
  getAllCollaborativeChartsReviews,
  getCollaborativeChartsReview,
  getCollaborativeChartsReviewForMatch,
  getCollaborativeChartsReviewItems,
  submitCollaborativeChartsReview,
  updateCollaborativeChartsReviewItemQuestionnaire,
  getCollaborativeChartsReviewClinicalFlags,
  CollaborativeChartsReviewItemClinicalFlag,
  deleteCollaborativeChartsReviewItemClinicalFlag,
  UpdateCollaborativeChartsReviewItemClinicalFlagFields,
  updateCollaborativeChartsReviewItemClinicalFlag,
  updateCollaborativeChartsReview,
  UpdateCollaborativeChartsReviewParams
} from '@uplift/api';


const keys = {
  chartsReviews: () => [ 'collaboration', 'chartsReview' ],
  chartsReviewsWithParams: (params: AllCollaborativeChartsReviewsParams) => [ 'collaboration', 'chartsReview', params ],
  chartsReviewForMatch: (matchId: string) => [ 'collaboration', 'chartsReview', { matchId } ],
  chartsReview: (reviewId: string) => [ 'collaboration', 'chartsReview', { reviewId } ],
  chartsReviewItems: (reviewId: string) => [ 'collaboration', 'chartsReview', { reviewId }, 'items' ],
  chartsReviewClinicalFlags: (reviewId: string) => [ 'collaboration', 'chartsReview', { reviewId }, 'flags' ],
};


export const useAllCollaborativeChartsReviewsQuery = (params?: AllCollaborativeChartsReviewsParams, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: params ? keys.chartsReviewsWithParams(params) : keys.chartsReviews(),
    queryFn: async () => {
      const response = await getAllCollaborativeChartsReviews(params);

      return response.data;
    },
    ...opts,
  });
};

export const useCollaborativeChartsReviewForMatchQuery = (matchId: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: keys.chartsReviewForMatch(matchId),
    queryFn: async () => {
      const response = await getCollaborativeChartsReviewForMatch(matchId);

      return response.data;
    },
    ...opts,
  });
};

export const useCollaborativeChartsReviewQuery = (reviewId: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: keys.chartsReview(reviewId),
    queryFn: async () => {
      const response = await getCollaborativeChartsReview(reviewId);

      return response.data;
    },
    ...opts,
  });
};


export const useCollaborativeChartsReviewItemsQuery = (reviewId: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: keys.chartsReviewItems(reviewId),
    queryFn: async () => {
      const response = await getCollaborativeChartsReviewItems(reviewId);

      return response.data;
    },
    ...opts,
  });
};


export const useUpdateCollaborativeChartsReviewMutation = (reviewId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateCollaborativeChartsReviewParams) => {
      const response = await updateCollaborativeChartsReview(reviewId, params);

      return response;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: keys.chartsReview(reviewId) });
    },
  });
};


export const useSubmitCollaborativeChartsReviewMutation = (reviewId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await submitCollaborativeChartsReview(reviewId);

      return response.data;
    },
    async onSuccess(data) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.chartsReviews() }),
        queryClient.invalidateQueries({ queryKey: keys.chartsReview(reviewId) }),
        queryClient.invalidateQueries({ queryKey: keys.chartsReviewForMatch(data.matchId) }),
      ]);

    },
  });
};

export const useUpdateCollaborativeChartsReviewItemMutation = (reviewId: string, resourceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: ChartsReviewItemQuestionnaireFields) => {
      const response = await updateCollaborativeChartsReviewItemQuestionnaire(reviewId, resourceId, args);

      return response;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: keys.chartsReviewItems(reviewId) });
    },
  });
};


export const useCollaborativeChartsReviewClinicalFlagsQuery = (reviewId: string) => {
  return useSuspenseQuery({
    queryKey: keys.chartsReviewClinicalFlags(reviewId),
    queryFn: async () => {
      const response = await getCollaborativeChartsReviewClinicalFlags(reviewId);

      return response.data;
    },
    select: res => res.reduce((prev, curr) => ({
      ...prev,
      [curr.resourceId]: curr,

    }), {} as Record<string, CollaborativeChartsReviewItemClinicalFlag>)
  });
};


export const useUpdateCollaborativeChartsReviewItemClinicalFlagMutation = (reviewId: string, resourceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: UpdateCollaborativeChartsReviewItemClinicalFlagFields) => {
      const response = await updateCollaborativeChartsReviewItemClinicalFlag(reviewId, resourceId, args);

      return response;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: keys.chartsReviewClinicalFlags(reviewId) });
    },
  });
};

export const useDeleteCollaborativeChartsReviewItemClinicalFlagMutation = (reviewId: string, resourceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await deleteCollaborativeChartsReviewItemClinicalFlag(reviewId, resourceId);

      return response;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: keys.chartsReviewClinicalFlags(reviewId) });
    },
  });
};
